import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AlertCustomComponent } from 'pharedata-ng-components';
import { EllipsisTooltipDirective } from '../../shared/ellipsis-tooltip.directive';
import { TableScrollSyncDirective } from '../../shared/table-scroll-sync.directive';
import { ParentMessagesSender } from '../../services/parent-messages-sender';
import { GlobalState } from '../../services/global-state';
import { LlamanosApiService } from '../../services/api-llamanos.service';
import { ExternalApisService } from '../../services/external-apis.service';
import { getCategorias } from '../../utils/categorias';
import { getPeriodosDesde } from '../../utils/timeZone';
import { VerCasoComponent } from '../../shared/ver-caso/ver-caso.component';
import { ESTADOS_CASOS, formatFecha } from '../../utils/utils';

@Component({
  selector: 'app-historial-llamanos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    VerCasoComponent,
    AlertCustomComponent,
    EllipsisTooltipDirective,
    TableScrollSyncDirective,
  ],
  templateUrl: './historial-llamanos.component.html',
  styleUrl: './historial-llamanos.component.scss',
})
export class HistorialLlamanosComponent implements OnInit {
  // Datos para Estación de Servicio
  edsOptions: Array<{ label: string; value: number; desde: Date }> = [];

  // Datos para Período
  periodos: Array<{ name: string; id: string }> = [];

  // Datos para Categoría (extraídas de los casos)
  categorias: Array<{ name: string }> = [];

  // Datos para Subcategoría (extraídas de los casos según categoría seleccionada)
  subcategorias: Array<{ name: string }> = [];

  // Datos para Estado (extraídos de los casos)
  estados: Array<{ name: string }> = [];

  // Variables para los valores seleccionados
  selectedEstacion: number | null = null;
  selectedPeriodo: any = null;
  selectedCategoria: string | null = null;
  selectedSubcategoria: string | null = null;
  selectedEstado: string | null = null;
  searchTerm: string = '';
  casos: any = [];
  private casosOriginales: any = []; // Casos sin filtrar por periodo
  private categoriasRaw: Record<string, any> = {}; // Para validación en onVerCaso
  private categoriasAllRaw: Record<string, any> = {}; // Incluye subcategorías inactivas
  showVerCaso: boolean = false;
  casoSeleccionado: any = null;
  subcategoriaCompleta: any = null;
  showAlert: boolean = false;
  alertContent: string = '';
  alertIcon: string = '';
  ESTADOS_CASOS: any = ESTADOS_CASOS;
  formatFecha = formatFecha;

  isArcoprime: boolean = false;
  pagadores: Array<{ id_pagador: string; label: string }> = [];
  selectedPagador: string | null = null;
  hasSearched: boolean = false;

  constructor(
    private globalState: GlobalState,
    private api: LlamanosApiService,
    private parentMessagesSender: ParentMessagesSender,
    private externalApis: ExternalApisService,
  ) {}

  getApelacionCaseNumber(subject: unknown): string | null {
    const s = String(subject || '').trim();
    const match = /^Apelación de caso llámanos (\d+) .+/.exec(s);
    return match ? match[1] : null;
  }

  ngOnInit(): void {
    this.globalState.isArcoprime$.subscribe((v) => (this.isArcoprime = v));

    this.globalState.pagadores$.subscribe((pagadores) => {
      if (!this.isArcoprime) return;
      this.pagadores = pagadores || [];
      if (this.pagadores.length === 1) {
        this.onPagadorChange(this.pagadores[0].id_pagador);
      }
    });

    // Cargar opciones de estaciones desde GlobalState (no arcoprime)
    this.globalState.allEds$.subscribe((eds) => {
      if (this.isArcoprime) return;
      this.edsOptions = eds || [];
      if (this.edsOptions.length === 1) {
        this.selectedEstacion = this.edsOptions[0]?.value;
        this.updatePeriodos();
        this.loadCasos();
      }
    });

    // Cargar categorías desde api-llamanos (misma lógica que en LlamanosComponent, vía util)
    this.api.getCategorias().subscribe({
      next: (data) => {
        this.categoriasAllRaw = (data || {}) as Record<string, any>;
        const filteredSource = getCategorias(data);

        this.categoriasRaw = filteredSource;

        // Rellenar dropdown de categorías reutilizando la forma actual (id, name)
        this.categorias = Object.keys(filteredSource).map((key, index) => ({
          id: index + 1,
          name: String(key),
        }));

        // Recalcular hasValidCategory para casos ya cargados
        if (this.casosOriginales.length > 0) {
          this.casosOriginales.forEach((caso: any) => {
            caso.hasValidCategory = this.checkValidCategoryAndSubcategory(caso);
          });
          this.applyFilters();
        }
      },
      error: (err) => {
        console.error('Error cargando categorias en historial-llamanos: ', err);
      },
    });
  }

  onPagadorChange(idPagador: string): void {
    this.selectedPagador = idPagador;
    this.selectedEstacion = null;
    this.selectedPeriodo = null;
    this.selectedCategoria = null;
    this.selectedSubcategoria = null;
    this.selectedEstado = null;
    this.edsOptions = [];
    this.periodos = [];
    this.categorias = [];
    this.subcategorias = [];
    this.estados = [];
    this.casos = [];
    this.casosOriginales = [];
    this.hasSearched = false;
    if (!idPagador) return;
    this.globalState.pagador = idPagador;
    this.externalApis.getEdsForPagador(idPagador, true).subscribe((eds) => {
      this.edsOptions = eds || [];
      if (this.edsOptions.length === 1) {
        this.selectedEstacion = this.edsOptions[0]?.value;
        this.updatePeriodos();
        this.loadCasos();
      }
    });
  }

  loadCasos(onLoaded?: () => void): void {
    if (this.selectedEstacion) {
      this.showAlert = false;
      // Obtener la estación completa para acceder a la propiedad 'desde'
      const estacionCompleta = this.edsOptions.find(
        (eds) => eds.value === this.selectedEstacion,
      );

      if (!estacionCompleta || !estacionCompleta.desde) {
        this.casos = [];
        return;
      }

      this.hasSearched = true;
      this.api.getCaso(this.selectedEstacion).subscribe({
        next: (data) => {
          // Filtrar casos que tengan fechaCreacionCaso >= desde de la estación
          // y categoria != Siniestro
          const todosLosCasos = data.records || [];
          const casosFiltrados = (data.records || []).filter((record: any) => {
            if (!record.FechaCreacion__c) return false;
            if (record.Categoria1__c == 'Siniestro') return false;

            const iso = record.FechaCreacion__c.replace('+0000', 'Z');
            const fechaCreacionCaso = new Date(iso);

            // Comparar fechas (sin horas, solo fecha)
            const fechaDesde = new Date(estacionCompleta.desde);
            fechaDesde.setHours(0, 0, 0, 0);
            const fechaCaso = new Date(fechaCreacionCaso);
            fechaCaso.setHours(0, 0, 0, 0);

            return fechaCaso >= fechaDesde;
          });

          // Calcular y asignar hasValidCategory a cada caso
          casosFiltrados.forEach((caso: any) => {
            caso.hasValidCategory = this.checkValidCategoryAndSubcategory(caso);
            const numeroCasoReferenciado = this.getApelacionCaseNumber(
              caso.Subject,
            );
            caso.numeroCasoReferenciado = numeroCasoReferenciado;
            if (numeroCasoReferenciado) {
              caso.casoReferenciado = todosLosCasos.find(
                (c: any) => c.CaseNumber == numeroCasoReferenciado,
              );
            }
          });

          // Marcar casos que tienen apelación asociada (cuando otro caso lo referencia)
          const casosPorNumero = new Map<string, any>();
          casosFiltrados.forEach((c: any) => {
            const n = c?.CaseNumber;
            if (n !== null && n !== undefined && String(n).trim() !== '') {
              casosPorNumero.set(String(n), c);
            }
          });

          casosFiltrados.forEach((caso: any) => {
            const ref = caso?.numeroCasoReferenciado;
            if (!ref) return;
            const referenciado = casosPorNumero.get(String(ref));
            if (referenciado) {
              referenciado.hasApelacion = true;
            }
          });

          // Guardar casos originales (filtrados por fecha desde)
          this.casosOriginales = casosFiltrados;
          // Extraer categorías únicas de los casos
          this.extractCategoriasFromCasos();
          // Extraer estados únicos de los casos
          this.extractEstadosFromCasos();
          // Aplicar filtro de periodo si hay uno seleccionado
          this.applyFilters();

          if (onLoaded) {
            onLoaded();
          }
        },
        error: (err) => {
          this.alertContent =
            'Error en servicio Salesforce, por favor inténtalo de nuevo.';
          this.alertIcon = 'error';
          this.showAlert = true;
          console.error('Error cargando casos:', err);

          if (onLoaded) {
            onLoaded();
          }
        },
      });
    }
  }

  closeAlert = () => {
    this.showAlert = false;
    this.alertIcon = '';
  };

  hideGreyBackground = () => {
    this.parentMessagesSender.hideGreyBackground();
  };

  showGreyBackground = () => {
    this.parentMessagesSender.showGreyBackground();
  };

  scrollTo = (x: number, y: number) => {
    this.parentMessagesSender.scrollTo(x, y);
  };

  onPeriodoChange(periodoId: string | null): void {
    this.selectedPeriodo = periodoId;
    this.applyFilters();
  }

  onCategoriaChange(categoriaName: string | null): void {
    this.selectedCategoria = categoriaName;
    this.selectedSubcategoria = null; // Reset subcategoría cuando cambia categoría
    this.updateSubcategorias();
    this.applyFilters();
  }

  private extractCategoriasFromCasos(): void {
    const categoriasSet = new Set<string>();

    this.casosOriginales.forEach((caso: any) => {
      if (caso.Categoria1__c && typeof caso.Categoria1__c === 'string') {
        categoriasSet.add(caso.Categoria1__c.trim());
      }
    });

    this.categorias = Array.from(categoriasSet)
      .sort()
      .map((cat) => ({ name: cat }));
  }

  private updateSubcategorias(): void {
    if (!this.selectedCategoria) {
      this.subcategorias = [];
      return;
    }

    const subcategoriasSet = new Set<string>();

    this.casosOriginales.forEach((caso: any) => {
      if (
        caso.Categoria1__c === this.selectedCategoria &&
        caso.Categoria2__c &&
        typeof caso.Categoria2__c === 'string'
      ) {
        subcategoriasSet.add(caso.Categoria2__c.trim());
      }
    });

    this.subcategorias = Array.from(subcategoriasSet)
      .sort()
      .map((subcat) => ({ name: subcat }));
  }

  private extractEstadosFromCasos(): void {
    const estadosSet = new Set<string>();

    this.casosOriginales.forEach((caso: any) => {
      if (caso.Status && typeof caso.Status === 'string') {
        estadosSet.add(caso.Status.trim());
      }
    });

    this.estados = Array.from(estadosSet)
      .sort()
      .map((estado) => ({ name: estado }));
  }

  applyFilters(): void {
    let casosFiltrados = [...this.casosOriginales];

    // Filtrar por periodo si está seleccionado
    if (this.selectedPeriodo) {
      casosFiltrados = casosFiltrados.filter((caso: any) => {
        if (!caso.FechaCreacion__c) return false;

        const iso = caso.FechaCreacion__c.replace('+0000', 'Z');
        const fechaCreacionCaso = new Date(iso);
        const añoCaso = fechaCreacionCaso.getFullYear();
        const mesCaso = fechaCreacionCaso.getMonth() + 1; // getMonth() retorna 0-11
        const periodoCaso = `${añoCaso}${String(mesCaso).padStart(2, '0')}`;

        return periodoCaso === this.selectedPeriodo;
      });
    }

    // Filtrar por categoría si está seleccionada
    if (this.selectedCategoria) {
      casosFiltrados = casosFiltrados.filter(
        (caso: any) => caso.Categoria1__c === this.selectedCategoria,
      );
    }

    // Filtrar por subcategoría si está seleccionada
    if (this.selectedSubcategoria) {
      casosFiltrados = casosFiltrados.filter(
        (caso: any) => caso.Categoria2__c === this.selectedSubcategoria,
      );
    }

    // Filtrar por estado si está seleccionado
    if (this.selectedEstado) {
      casosFiltrados = casosFiltrados.filter(
        (caso: any) => caso.Status === this.selectedEstado,
      );
    }

    // Filtrar por término de búsqueda
    if (this.searchTerm && this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      casosFiltrados = casosFiltrados.filter((caso: any) => {
        return (
          String(caso.CaseNumber || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Categoria1__c || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Categoria2__c || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Status || '')
            .toLowerCase()
            .includes(searchLower) ||
          String(caso.Origin || '')
            .toLowerCase()
            .includes(searchLower)
        );
      });
    }

    this.casos = casosFiltrados;
  }

  onVerCaso(caso: any): void {
    const categoria1 = caso?.Categoria1__c;
    const categoria2 = caso?.Categoria2__c;

    this.subcategoriaCompleta = null;

    const categoriaEntry = this.categoriasAllRaw?.[categoria1];
    if (!Array.isArray(categoriaEntry)) {
      this.subcategoriaCompleta = null;
      this.casoSeleccionado = caso;
      this.showVerCaso = true;
      return;
    }

    const rawSubcats: any[] = categoriaEntry;
    for (const sub of rawSubcats) {
      if (!sub || typeof sub !== 'object') continue;
      const entries = Object.entries(sub);
      if (entries.length === 0) continue;
      const [subKey, subcategoriaData] = entries[0];
      if (subKey === categoria2) {
        this.subcategoriaCompleta = {
          key: subKey,
          label: subKey,
          ...(subcategoriaData as any),
        };
        break;
      }
    }

    this.casoSeleccionado = caso;
    this.showVerCaso = true;
  }

  onApelacionIngresada(event: {
    originalCaseNumber: string;
    nuevoCaseNumber?: string;
  }): void {
    const currentId = String(this.casoSeleccionado?.Id ?? '');
    if (!currentId) return;

    const wasShowingCaso = this.showVerCaso;
    this.loadCasos(() => {
      if (!wasShowingCaso) return;

      const updated = (this.casosOriginales || []).find(
        (c: any) => String(c?.Id ?? '') === currentId,
      );
      if (updated) {
        this.casoSeleccionado = updated;
      }
    });
  }

  // Private method to check if a case has valid category and subcategory
  private checkValidCategoryAndSubcategory(caso: any): boolean {
    const categoria1 = caso?.Categoria1__c;
    const categoria2 = caso?.Categoria2__c;

    if (!categoria1 || !categoria2) {
      return false;
    }

    const categoriaEntry = this.categoriasRaw?.[categoria1];
    const existsCategoria = Array.isArray(categoriaEntry);

    if (!existsCategoria) {
      return false;
    }

    const rawSubcats: any[] = categoriaEntry;
    for (const sub of rawSubcats) {
      if (!sub || typeof sub !== 'object') continue;
      const entries = Object.entries(sub);
      if (entries.length === 0) continue;
      const [subKey] = entries[0];
      if (subKey === categoria2) {
        return true;
      }
    }

    return false;
  }

  onCloseVerCaso(): void {
    this.showVerCaso = false;
    this.casoSeleccionado = null;
    this.subcategoriaCompleta = null;
  }

  get selectedEstacionComplete(): {
    label: string;
    value: number;
    desde: Date;
  } | null {
    if (!this.selectedEstacion) return null;
    return (
      this.edsOptions.find((eds) => eds.value === this.selectedEstacion) || null
    );
  }

  onEstacionChange(estacionValue: number): void {
    this.selectedEstacion = estacionValue;
    this.selectedCategoria = null;
    this.selectedSubcategoria = null;
    this.selectedEstado = null;
    this.categorias = [];
    this.subcategorias = [];
    this.estados = [];
    this.updatePeriodos();
    this.loadCasos();
  }

  private updatePeriodos(): void {
    if (!this.selectedEstacion) {
      this.periodos = [];
      return;
    }

    const estacionCompleta = this.edsOptions.find(
      (eds) => eds.value === this.selectedEstacion,
    );

    if (!estacionCompleta || !estacionCompleta.desde) {
      this.periodos = [];
      return;
    }

    this.periodos = getPeriodosDesde(estacionCompleta.desde);
  }
}
